import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export const providerService = {
  // Create a service provider invitation
  async createInvitation(invitationData) {
    try {
      const docRef = await addDoc(collection(db, 'providerInvitations'), {
        ...invitationData,
        status: 'pending', // pending, accepted, declined, expired
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Invite a service provider (wrapper for createInvitation)
  async inviteProvider(ownerId, invitationData) {
    try {
      const docRef = await addDoc(collection(db, 'providerInvitations'), {
        ownerId,
        ...invitationData,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Generate a unique referral link for an owner
  async generateReferralLink(ownerId, userData) {
    try {
      // Create or get existing referral code
      const referralCode = `${userData?.firstName || 'owner'}-${ownerId.slice(-6)}`;
      const baseUrl = window.location.origin;
      const referralLink = `${baseUrl}/register/provider?ref=${referralCode}`;
      
      // Store referral code in user's document for tracking
      const userRef = doc(db, 'users', ownerId);
      await updateDoc(userRef, {
        referralCode,
        referralLink,
        updatedAt: new Date()
      });
      
      return referralLink;
    } catch (error) {
      throw error;
    }
  },

  // Get invitation by ID
  async getInvitation(invitationId) {
    try {
      const docRef = doc(db, 'providerInvitations', invitationId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update invitation status
  async updateInvitationStatus(invitationId, status) {
    try {
      const docRef = doc(db, 'providerInvitations', invitationId);
      await updateDoc(docRef, {
        status,
        updatedAt: new Date()
      });
    } catch (error) {
      throw error;
    }
  },

  // Get invitations by owner
  async getInvitationsByOwner(ownerId) {
    try {
      const q = query(
        collection(db, 'providerInvitations'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const invitations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort invitations client-side for now
      invitations.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return invitations;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to invitations by owner
  subscribeToInvitationsByOwner(ownerId, callback) {
    try {
      const q = query(
        collection(db, 'providerInvitations'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );

      return onSnapshot(q, (querySnapshot) => {
        const invitations = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort invitations client-side for now
        invitations.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(invitations);
      }, (error) => {
        console.error('Error in invitation subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up invitation subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Add a service provider to saved/favorites
  async addToSaved(ownerId, providerId, providerData) {
    try {
      const docRef = await addDoc(collection(db, 'savedProviders'), {
        ownerId,
        providerId,
        providerData,
        savedAt: new Date(),
        lastInteraction: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Remove a service provider from saved/favorites
  async removeFromSaved(ownerId, providerId) {
    try {
      const q = query(
        collection(db, 'savedProviders'),
        where('ownerId', '==', ownerId),
        where('providerId', '==', providerId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const docRef = doc(db, 'savedProviders', querySnapshot.docs[0].id);
        await deleteDoc(docRef);
      }
    } catch (error) {
      throw error;
    }
  },

  // Get saved providers by owner
  async getSavedProvidersByOwner(ownerId) {
    try {
      const q = query(
        collection(db, 'savedProviders'),
        where('ownerId', '==', ownerId)
      );
      const querySnapshot = await getDocs(q);
      const savedProviders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by last interaction date
      savedProviders.sort((a, b) => {
        const dateA = a.lastInteraction?.toDate?.() || new Date(a.lastInteraction);
        const dateB = b.lastInteraction?.toDate?.() || new Date(b.lastInteraction);
        return dateB - dateA; // Descending order
      });
      
      return savedProviders;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to saved providers by owner
  subscribeToSavedProvidersByOwner(ownerId, callback) {
    try {
      const q = query(
        collection(db, 'savedProviders'),
        where('ownerId', '==', ownerId)
      );

      return onSnapshot(q, async (querySnapshot) => {
        const savedProviders = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by last interaction date
        savedProviders.sort((a, b) => {
          const dateA = a.lastInteraction?.toDate?.() || new Date(a.lastInteraction);
          const dateB = b.lastInteraction?.toDate?.() || new Date(b.lastInteraction);
          return dateB - dateA; // Descending order
        });
        
        callback(savedProviders);
      }, (error) => {
        console.error('Error in saved providers subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up saved providers subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Update last interaction date for a saved provider
  async updateLastInteraction(ownerId, providerId) {
    try {
      const q = query(
        collection(db, 'savedProviders'),
        where('ownerId', '==', ownerId),
        where('providerId', '==', providerId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const docRef = doc(db, 'savedProviders', querySnapshot.docs[0].id);
        await updateDoc(docRef, {
          lastInteraction: new Date()
        });
      }
    } catch (error) {
      throw error;
    }
  },

  // Get all available service providers on the platform
  async getAvailableProviders() {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'serviceProvider')
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const providers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort providers client-side for now
      providers.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return providers;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to all available service providers
  subscribeToAvailableProviders(callback) {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'serviceProvider')
        // Temporarily removed orderBy to avoid index requirement
      );

      return onSnapshot(q, (querySnapshot) => {
        const providers = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort providers client-side for now
        providers.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(providers);
      }, (error) => {
        console.error('Error in provider subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up provider subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Get referral statistics for an owner
  async getReferralStats(ownerId) {
    try {
      const q = query(
        collection(db, 'providerInvitations'),
        where('ownerId', '==', ownerId)
      );
      const querySnapshot = await getDocs(q);
      const invitations = querySnapshot.docs.map(doc => doc.data());
      
      return {
        totalInvitations: invitations.length,
        acceptedInvitations: invitations.filter(inv => inv.status === 'accepted').length,
        pendingInvitations: invitations.filter(inv => inv.status === 'pending').length,
        declinedInvitations: invitations.filter(inv => inv.status === 'declined').length,
        referralEarnings: invitations.filter(inv => inv.status === 'accepted').length * 50 // $50 per successful referral
      };
    } catch (error) {
      throw error;
    }
  },

  // Create a referral bonus record
  async createReferralBonus(bonusData) {
    try {
      const docRef = await addDoc(collection(db, 'referralBonuses'), {
        ...bonusData,
        createdAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Create a referral record when someone joins via referral link
  async createReferralRecord(ownerId, referralData) {
    try {
      const docRef = await addDoc(collection(db, 'providerInvitations'), {
        ownerId,
        ...referralData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get referral bonuses by owner
  async getReferralBonusesByOwner(ownerId) {
    try {
      const q = query(
        collection(db, 'referralBonuses'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const bonuses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort bonuses client-side for now
      bonuses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return bonuses;
    } catch (error) {
      throw error;
    }
  }
}; 